import { formatVoiceLabel } from '@app/modules/logger/screens/stream/components/audit/transcriptions/utils';
import {
  TranscriptionDiarizationItemDto,
  TranscriptionDto,
  TranscriptionWordItemDto,
} from '@mi-front/api-clients';
import { DateTimeStr, toTimestamp } from '@mi-front/core';
import { normalizeWordText } from './normalize-word-text';
import { splitDiarizationBlocks } from './transcription-block-splitter';
import { TranscriptionBlockData, TranscriptionWordData } from './transcription.types';

const speakerColors = [
  '#FF9AA2',
  '#7EAAC7',
  '#66E2D3',
  '#D8B384',
  '#A39EC9',
  '#FF8C42',
  '#70D6B3',
  '#FF6F61',
  '#9F94F0',
  '#FFA07A',
];

/** Diarization rows may include per-segment words when the API provides them. */
type DiarizationWithOptionalWords = TranscriptionDiarizationItemDto & {
  words?: TranscriptionWordItemDto[];
};

/**
 * Formats seconds (fractional) as m:ss or h:mm:ss for transcript timestamps.
 */
function formatOffsetSecondsAsLabel(totalSeconds: number): string {
  const sec = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (h > 0) {
    return `${h}:${pad(m)}:${pad(s)}`;
  }
  return `${m}:${pad(s)}`;
}

/**
 * Picks word timings that overlap a diarization segment (seconds relative to the chunk).
 */
function wordsOverlappingSegment(
  wpWords: TranscriptionWordItemDto[],
  segment: { start: number; end: number },
): TranscriptionWordItemDto[] {
  return wpWords.filter((w) => w.start < segment.end && w.end > segment.start);
}

function mapWordDto(
  words: TranscriptionWordItemDto[],
  blockPrefix: string,
): TranscriptionWordData[] {
  return words.map((w, index) => ({
    id: `${blockPrefix}-w-${index}`,
    start: w.start,
    end: w.end,
    adjustedEnd: w.end,
    text: w.word,
    normalizedText: normalizeWordText(w.word),
    confidence: w.confidence,
  }));
}

/**
 * Absolute playback time (ms) for a block's segment start (chunk anchor + offset seconds).
 */
function blockAbsoluteStartMs(block: TranscriptionBlockData): number {
  return toTimestamp(block.chunkTime) + block.segment.start * 1000;
}

/**
 * Orders blocks chronologically so the transcript list renders in timeline order.
 */
function sortBlocksByTime(blocks: TranscriptionBlockData[]): void {
  blocks.sort((a, b) => {
    const startDiff = blockAbsoluteStartMs(a) - blockAbsoluteStartMs(b);
    if (startDiff !== 0) {
      return startDiff;
    }

    const endDiff = a.segment.end - b.segment.end;
    if (endDiff !== 0) {
      return endDiff;
    }

    return a.id.localeCompare(b.id);
  });
}

/**
 * Sets `segment.adjustedEnd` so highlight stays on each block until the next block's start.
 * Then sets each word's `adjustedEnd` to the next word's start or the block's `adjustedEnd`.
 */
function applyHighlightAdjustedEnds(blocks: TranscriptionBlockData[]): void {
  if (blocks.length === 0) {
    return;
  }

  const order = blocks.map((_, index) => index);
  order.sort((a, b) => {
    const diff = blockAbsoluteStartMs(blocks[a]) - blockAbsoluteStartMs(blocks[b]);
    return diff !== 0 ? diff : a - b;
  });

  for (let o = 0; o < order.length; o++) {
    const block = blocks[order[o]];
    const nextIndex = order[o + 1];
    const chunkMs = toTimestamp(block.chunkTime);

    if (nextIndex === undefined) {
      block.segment.adjustedEnd = block.segment.end;
    } else {
      const next = blocks[nextIndex];
      const nextAbsStartMs = toTimestamp(next.chunkTime) + next.segment.start * 1000;
      block.segment.adjustedEnd = (nextAbsStartMs - chunkMs) / 1000;
    }

    if (block.isMusic || block.words.length === 0) {
      continue;
    }

    const wordIndices = block.words.map((_, i) => i);
    wordIndices.sort((a, b) => {
      const d = block.words[a].start - block.words[b].start;
      return d !== 0 ? d : a - b;
    });

    for (let w = 0; w < wordIndices.length; w++) {
      const word = block.words[wordIndices[w]];
      const nextBoundary =
        w + 1 < wordIndices.length
          ? block.words[wordIndices[w + 1]].start
          : block.segment.adjustedEnd;
      word.adjustedEnd = nextBoundary;
    }
  }
}

/**
 * Maps a BFF transcription DTO into flat transcript blocks for the V2 panel.
 * Diarization segments may be split into smaller sub-blocks for readability.
 */
export function mapTranscriptionDtoToBlocks(dto: TranscriptionDto): TranscriptionBlockData[] {
  const blocks: TranscriptionBlockData[] = [];
  const wpWords = dto.wp_words ?? [];
  const chunkTime = dto.time as DateTimeStr;
  const diarization = (dto.diarization ?? []) as DiarizationWithOptionalWords[];
  const speakerColorByVoiceLabel = new Map<string, string>();

  diarization.forEach((d, diIndex) => {
    if (!d || d.label === 'SILENCE') {
      return;
    }

    const speakerLabel =
      d.voice_label !== 'NO IDENTIFICADO' ? d.voice_label : formatVoiceLabel(d.label);
    const speakerColor =
      speakerColorByVoiceLabel.get(speakerLabel) ??
      speakerColors[speakerColorByVoiceLabel.size % speakerColors.length];

    if (!speakerColorByVoiceLabel.has(speakerLabel)) {
      speakerColorByVoiceLabel.set(speakerLabel, speakerColor);
    }

    const wordsSource =
      d.words && d.words.length > 0 ? d.words : wordsOverlappingSegment(wpWords, d.segment);

    const blockPrefix = `d${diIndex}`;

    if (!wordsSource.length) {
      return;
    }

    const subBlocks = splitDiarizationBlocks(d.segment, wordsSource);

    subBlocks.forEach((subBlock, subIndex) => {
      const subBlockPrefix = `${blockPrefix}-s${subIndex}`;
      blocks.push({
        id: `${subBlockPrefix}-${subBlock.segment.start}-${d.label}`,
        chunkTime,
        segment: {
          start: subBlock.segment.start,
          end: subBlock.segment.end,
          adjustedEnd: subBlock.segment.end,
        },
        timeLabel: formatOffsetSecondsAsLabel(subBlock.segment.start),
        words: mapWordDto(subBlock.words, subBlockPrefix),
        speakerLabel,
        speakerColor,
        isMusic: false,
      });
    });
  });

  sortBlocksByTime(blocks);
  applyHighlightAdjustedEnds(blocks);

  return blocks;
}
